/**
 * Bridge route for the Core Daily Report SSO deep-link flow.
 *
 * Triggered when Core Daily Report detects an unauthenticated request and
 * redirects to:
 *     /ai/auth/core-daily-report-redirect?return=<original path>
 *
 * Must be a top-level route (NOT wrapped in <ProtectedRoute>) so the bridge can
 * preserve the return path while bouncing unauthenticated users through /login.
 */

import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useAuthStore from '../../../../stores/authStore';
import { authenticatedFetch } from '../../../../utils/apiClient';

export default function CoreDailyReportRedirect() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const returnPath = params.get('return') ?? '/';

  const isInitialized = useAuthStore(s => s.isInitialized);
  const accessToken = useAuthStore(s => s.accessToken);

  useEffect(() => {
    if (!isInitialized) return;

    if (!accessToken) {
      const here = `/auth/core-daily-report-redirect?return=${encodeURIComponent(returnPath)}`;
      navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch(
          `/api/core-daily-report/sso-link?return_path=${encodeURIComponent(returnPath)}`
        );
        if (cancelled) return;

        if (!response.ok) {
          console.error('[core-daily-report-redirect] sso-link failed:', response.status);
          navigate('/', { replace: true });
          return;
        }
        const data = await response.json();
        if (data?.url) {
          window.location.replace(data.url);
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[core-daily-report-redirect] error:', err);
          navigate('/', { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isInitialized, accessToken, returnPath, navigate]);

  return (
    <div
      style={{
        padding: 48,
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {isInitialized ? 'Redirecting to Core Daily Report...' : 'Loading...'}
    </div>
  );
}
